import { EmptyState } from '@uroute/ui';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { JobCard } from '../components/JobCard';
import { JobsSkeleton } from '../components/Skeleton';
import { PullToRefresh } from '../components/PullToRefresh';
import { Search, Inbox } from '../components/icons';
import { useMyJobs } from '../lib/jobs';
import { formatUpdated } from '../lib/offline';

type Tab = 'active' | 'upcoming' | 'completed';

const TABS: { id: Tab; label: string }[] = [
  { id: 'active', label: 'Active' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'completed', label: 'Done' },
];

export function JobsPage() {
  const navigate = useNavigate();
  const view = useMyJobs();
  const [params] = useSearchParams();
  const sharedRef = params.get('ref') ?? '';
  const [tab, setTab] = useState<Tab>('active');
  const [q, setQ] = useState(sharedRef);

  const filter = (jobs: typeof view.jobs) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return jobs;
    return jobs.filter(
      (j) =>
        j.externalRef.toLowerCase().includes(needle) ||
        j.deliveryAddress.line1.toLowerCase().includes(needle) ||
        j.deliveryAddress.postcode.toLowerCase().includes(needle) ||
        j.deliveryAddress.city.toLowerCase().includes(needle) ||
        j.collectionAddress.line1.toLowerCase().includes(needle),
    );
  };

  const list = useMemo(() => {
    if (sharedRef) {
      const all = [
        ...view.active,
        ...view.upcoming,
        ...view.completedToday,
        ...view.completedEarlier,
      ];
      return filter(all);
    }
    if (tab === 'active') return filter(view.active);
    if (tab === 'upcoming') return filter(view.upcoming);
    return filter([...view.completedToday, ...view.completedEarlier]);
  }, [tab, view, q, sharedRef]);

  return (
    <PullToRefresh onRefresh={() => view.refresh()}>
      <header className="pagehead">
        <h1 className="pagehead__title">My jobs</h1>
        <div className="searchbox" data-testid="jobs-search">
          <Search size={16} aria-hidden="true" />
          <input
            className="searchbox__input"
            type="search"
            placeholder="Search ref, address, postcode"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search jobs"
          />
        </div>
      </header>

      <div className="tabs" role="tablist" aria-label="Job filters">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`tabs__tab${tab === t.id ? ' tabs__tab--active' : ''}`}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
            <span className="u-data tabs__count">
              {t.id === 'active' ? view.active.length : t.id === 'upcoming' ? view.upcoming.length : view.completedToday.length + view.completedEarlier.length}
            </span>
          </button>
        ))}
      </div>

      {view.loading ? (
        <JobsSkeleton count={4} />
      ) : list.length === 0 ? (
        <EmptyState
          icon={<Inbox size={40} />}
          title={q ? 'No matches' : 'Nothing here'}
          description={
            q
              ? 'Try a different reference or postcode.'
              : tab === 'active'
                ? 'No active jobs right now.'
                : tab === 'upcoming'
                  ? 'No upcoming assignments yet.'
                  : 'Completed jobs will appear here.'
          }
        />
      ) : (
        <ul className="joblist" data-testid="jobs-list">
          {list.map((job) => (
            <li key={job.id}>
              <JobCard job={job} onClick={() => navigate(`/jobs/${job.id}`)} />
            </li>
          ))}
        </ul>
      )}

      <p className="list-updated u-data" data-testid="jobs-updated">
        Updated {formatUpdated(view.updatedAt)}
        {view.refreshing ? ' · refreshing…' : ''}
      </p>
    </PullToRefresh>
  );
}
